import { useEffect, useMemo, useRef, useState } from 'react'
import * as sound from '../sound'
import { twMerge } from 'tailwind-merge'
import { PackPool, PackWeapon } from '../api/packs'
import commonBack from '../assets/cards/common_back.webp'
import commonFront from '../assets/cards/common_front.webp'
import legendaryBack from '../assets/cards/legendary_back.webp'
import ultraBack from '../assets/cards/ultra_back.webp'

/**
 * The opening sequence.
 *
 * Modelled on the card-ripping experience: a face-down card, then the weapon's
 * traits resolving one reel at a time, then the card turning over. The tension
 * comes from the stagger, not from the flip, which is why the reels land in
 * sequence rather than together and why rarity is always last.
 *
 * The spin is decoration and the result is not. Every reel scrolls through
 * values that are genuinely still drawable from this pack, supplied by the API
 * as `pool`, and always settles on the value the server actually returned. It
 * cannot show an outcome that did not happen, and it cannot tease a weapon
 * type the pack no longer holds.
 */

// Sequential slot timing: each reel waits (blurred), then spins (highlighted),
// then settles (clear) before the next begins — so exactly one reel is active
// at a time. Slower than a simultaneous spin, on purpose: it's a reveal.
const REEL_START_MS = [300, 1700, 3100]
const REEL_SPIN_MS = 1400
const FLIP_AT_MS = REEL_START_MS[2] + REEL_SPIN_MS + 500
// The card holds face-down while the slots run underneath it, and turns over
// once. It used to flip twice — sealed, then a middle face carrying the slots,
// then the weapon — because the slots were laid over the card. With them below
// it there is nothing to turn to in the middle, and the one flip left is the
// one the whole sequence is building to.
const INTRO_HOLD_MS = 250
const FLIP_MS = 700 // matches the CSS flip transition
// Slot-reel geometry: each reel is a vertical window this many rows tall, with
// the centre row as the pay line.
const ITEM_H = 30
const VISIBLE_ROWS = 3
const STRIP_LEN = 44

type ReelState = 'pending' | 'active' | 'settled'

type Reel = {
    label: string
    /** The real answer. */
    value: string | null
    /** What to scroll through on the way there. */
    options: string[]
    /** Rarity is the payoff: its highlight is the drawn rarity's colour. */
    accent?: boolean
    /** Highlight colour while this reel spins. The first two reels take the two
     *  brand-logo colours; the rarity reel (accent) uses the rarity colour. */
    highlight?: string
}

// The two brand colours from the BR1 logo gradient (cyan → magenta).
const BRAND_CYAN = '#03CEFF'
const BRAND_MAGENTA = '#C91FFF'

export const rarityColor = (rarity: string | null): string => {
    switch (rarity) {
        case 'Legendary':
            return '#FFD84D'
        case 'Ultra':
            return '#FF8A3D'
        case 'Epic':
            return '#C91FFF'
        case 'Rare':
            return '#03CEFF'
        default:
            return '#FFFFFF'
    }
}

/** Rarest first. Order the "what's inside" grid by this so the cool stuff leads.
 *  Unknown rarities (e.g. Common) sort to the end. */
export const RARITY_ORDER = ['Legendary', 'Ultra', 'Epic', 'Rare']
export const rarityRank = (rarity: string | null): number => {
    const i = RARITY_ORDER.indexOf(rarity ?? '')
    return i === -1 ? RARITY_ORDER.length : i
}

/**
 * The printed card art, in two layers.
 *
 * The delivered files are named for their layer, not for a side of the card:
 * every `*_back.webp` is an opaque full-bleed *background* (the perforated
 * panel, tinted per rarity) and `common_front.webp` is an RGBA *frame* — the
 * skull-and-wordmark bars, with a fully transparent middle for the weapon to
 * show through. So a face is background + weapon + frame, stacked, and the
 * "back" of the card in the flip sense is just the common background with
 * nothing over it.
 *
 * Common is the neutral one, so it is what the card shows face-down: a
 * rarity-tinted back would give the pull away before the reels had spun for it.
 */
const CARD_BACKGROUND: Record<string, string> = {
    Legendary: legendaryBack,
    Ultra: ultraBack,
}

/** Only the common frame has been drawn so far. Dropping `legendary_front.webp`
 *  (etc.) into `assets/cards/` and naming it here is all a per-rarity frame
 *  needs — nothing else reads these files. */
const CARD_FRAME: Record<string, string> = {}

/** The face-down card. Deliberately not keyed by rarity. */
export const CARD_BACK = commonBack

export const cardArt = (rarity: string | null) => ({
    background: CARD_BACKGROUND[rarity ?? ''] ?? commonBack,
    frame: CARD_FRAME[rarity ?? ''] ?? commonFront,
})

export const rarityGlow = (rarity: string | null): string => {
    const color = rarityColor(rarity)
    return `0 0 24px ${color}55, 0 0 64px ${color}22`
}

const reduceMotion = () =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * One slot reel: a vertical strip that spins down and decelerates onto the real
 * value, snapping it to the centre pay line. Rarity gets the accent glow when it
 * lands. The strip always *ends* on the true value, so the reel is a slot in
 * feel only — it can never stop on an outcome the server did not return.
 */
const SlotReel = ({ reel, spinMs, state }: { reel: Reel; spinMs: number; state: ReelState }) => {
    const reduce = reduceMotion()
    const settled = state === 'settled'
    const active = state === 'active'
    const pending = state === 'pending'

    const strip = useMemo(() => {
        const pool = reel.options.length ? reel.options : [reel.value ?? '']
        const arr = Array.from({ length: STRIP_LEN }, () => pool[Math.floor(Math.random() * pool.length)])
        // Land the real value a couple of rows from the end, so there is a row
        // below it to fill the bottom of the window.
        const valueIndex = STRIP_LEN - 2
        arr[valueIndex] = reel.value ?? ''
        return { arr, valueIndex }
        // Component is keyed by the draw, so a fresh mount is a fresh strip.
    }, [])

    // Offset that puts the value row on the centre pay line.
    const target = -((strip.valueIndex - Math.floor(VISIBLE_ROWS / 2)) * ITEM_H)
    const [y, setY] = useState(reduce ? target : 0)

    useEffect(() => {
        if (reduce) {
            setY(target)
            return
        }
        // Only travel once it is this reel's turn (or if it settled straight
        // away, e.g. on skip). A pending reel sits at the top, blurred.
        if (state === 'active' || state === 'settled') {
            const id = requestAnimationFrame(() => requestAnimationFrame(() => setY(target)))
            return () => cancelAnimationFrame(id)
        }
    }, [state, target, reduce])

    // Highlight colour: the rarity for the accent (rarity) reel, else this
    // reel's assigned brand colour.
    const accent = reel.accent ? rarityColor(reel.value) : reel.highlight ?? BRAND_CYAN

    // Type size, chosen from the LONGEST value this reel can ever show -- not
    // from the row being drawn, or the strip would resize as it scrolled past a
    // long word. "Legendary" is nine characters and was being elided to
    // "Legend..." at the fixed size, which is the one thing a reveal must not
    // do: the rarity is the payoff, and half of it is worse than none.
    //
    // Decided here rather than by measuring, because a reel has to be right on
    // its first painted frame and there is nothing rendered yet to measure.
    const widest = useMemo(
        () => Math.max(...[...reel.options, reel.value ?? ''].map((v) => v.length), 1),
        [reel.options, reel.value],
    )
    const valueSize = widest <= 7 ? 'text-sm' : widest <= 10 ? 'text-xs' : 'text-2xs'

    return (
        <div className='flex min-w-0 flex-1 flex-col items-center'>
            <div
                className='relative w-full overflow-hidden border transition-all duration-300'
                style={{
                    height: ITEM_H * VISIBLE_ROWS,
                    // The active reel is highlighted; pending reels dim back.
                    borderColor: active ? `${accent}AA` : 'transparent',
                    boxShadow: active ? `0 0 20px ${accent}55, inset 0 0 14px ${accent}22` : 'none',
                    opacity: pending ? 0.4 : 1,
                }}
            >
                {/* Centre pay line — brighter while this reel is spinning. */}
                <div
                    className='pointer-events-none absolute inset-x-1 top-1/2 z-10 -translate-y-1/2 border-y bg-white/[0.04] transition-colors'
                    style={{ height: ITEM_H, borderColor: active ? `${accent}99` : 'rgba(255,255,255,0.15)' }}
                />
                {/* Top/bottom fade, so items enter and leave the window softly. */}
                <div
                    className='pointer-events-none absolute inset-0 z-20'
                    style={{
                        background:
                            'linear-gradient(180deg, rgba(0,0,0,0.95), transparent 32%, transparent 68%, rgba(0,0,0,0.95))',
                    }}
                />
                <div
                    style={{
                        transform: `translateY(${y}px)`,
                        transition:
                            active && !reduce ? `transform ${spinMs}ms cubic-bezier(0.12,0.72,0.16,1)` : 'none',
                        // Pending: blurred (waiting). Active: light motion blur.
                        // Settled: crisp.
                        filter: settled ? 'none' : active ? 'blur(0.6px)' : 'blur(3px)',
                    }}
                >
                    {strip.arr.map((v, i) => {
                        const isValue = settled && i === strip.valueIndex
                        return (
                            <div
                                key={i}
                                className={twMerge(
                                    'flex items-center justify-center px-1 font-black uppercase tracking-tight',
                                    valueSize,
                                )}
                                style={{
                                    height: ITEM_H,
                                    ...(isValue && reel.accent
                                        ? { color: rarityColor(reel.value), textShadow: rarityGlow(reel.value) }
                                        : {}),
                                }}
                            >
                                <span className='truncate'>{v || '—'}</span>
                            </div>
                        )
                    })}
                </div>
            </div>
            {/* The label, set apart from the strip above it.
                Sitting flush under the window in the same casing, "WEAPON" read
                as a fourth value the reel might land on rather than as the name
                of the column. A rule and a gap say heading; proximity alone said
                option. Smaller and wider-tracked for the same reason -- a label
                should not compete with the thing it labels. */}
            <div
                className='mt-1 w-full border-t border-white/10 pt-1 text-center text-[9px] uppercase tracking-[0.2em] transition-colors'
                style={{ color: active ? accent : 'rgba(255,255,255,0.3)' }}
            >
                {reel.label}
            </div>
        </div>
    )
}

type Props = {
    weapon: PackWeapon
    /** Scenery for the reels: the pack's whole contents, claimed or not. NOT
     *  the drawable pool. A pack drained to its last unkept weapon of a trait
     *  leaves that reel one value, and a strip sampled from one value renders
     *  the same word on every row, which reads as broken rather than as a
     *  draw. Nothing here is a claim about what can still be won: the strip
     *  always ends on the value the server actually returned. */
    decoys: PackPool
    /** Bumped per draw, so a respin replays the whole sequence. */
    revealKey: number
    onFinished?: () => void
}

export const PackOpening = ({ weapon, decoys, revealKey, onFinished }: Props) => {
    // Two counters drive the reels: how many have started spinning, and how many
    // have settled. A reel is active when started but not settled.
    const [startedCount, setStartedCount] = useState(0)
    const [settledCount, setSettledCount] = useState(0)
    // The card turns over once: face-down, then the weapon. The rotation only
    // ever accumulates forward (+180 per flip), so a respin turns on from
    // wherever it is rather than spinning backwards. `revealed` is true only
    // once the weapon is shown.
    //
    // Face-down is every even multiple of 180 and the weapon every odd one, so
    // "which face is toward the viewer" is `rot % 360`.
    const SEALED_ROT = 0
    const rotationRef = useRef(SEALED_ROT)
    const firstDrawRef = useRef(true)
    const [rotation, setRotation] = useState(SEALED_ROT)
    const [revealed, setRevealed] = useState(false)

    // Reset the reels *synchronously* when a new draw arrives (open or respin),
    // before they re-render. Otherwise a respin's reels remount carrying the
    // previous reveal's finished counts, so they mount already settled and never
    // spin. React's documented "adjust state on prop change" pattern; the effect
    // below schedules the flips and reel timing.
    const [drawKey, setDrawKey] = useState(revealKey)
    if (drawKey !== revealKey) {
        setDrawKey(revealKey)
        setStartedCount(0)
        setSettledCount(0)
        setRevealed(false)
    }

    // `options` always contains the real value, so a decoy set of one still
    // reads as a reel rather than an empty box, and the drawn weapon appears
    // even in the edge case where it is somehow absent from the contents.
    const withValue = (options: string[], value: string | null) =>
        value && !options.includes(value) ? [...options, value] : options

    const reels: Reel[] = [
        {
            label: 'Weapon',
            value: weapon.weapon_type,
            options: withValue(decoys.weapon_types, weapon.weapon_type),
            highlight: BRAND_CYAN,
        },
        {
            label: 'Bonus',
            value: weapon.erb,
            options: withValue(decoys.erbs, weapon.erb),
            highlight: BRAND_MAGENTA,
        },
        {
            label: 'Rarity',
            value: weapon.rarity,
            options: withValue(decoys.rarities, weapon.rarity),
            accent: true,
        },
    ]

    const flipTo = (rot: number) => {
        rotationRef.current = rot
        setRotation(rot)
    }

    const skip = () => {
        setStartedCount(reels.length)
        setSettledCount(reels.length)
        // Snap to the next weapon-facing rotation.
        flipTo(rotationRef.current % 360 === 0 ? rotationRef.current + 180 : rotationRef.current)
        setRevealed(true)
        onFinished?.()
    }

    useEffect(() => {
        setStartedCount(0)
        setSettledCount(0)
        setRevealed(false)

        const first = firstDrawRef.current
        firstDrawRef.current = false

        if (reduceMotion()) {
            setStartedCount(REEL_START_MS.length)
            setSettledCount(REEL_START_MS.length)
            flipTo(rotationRef.current % 360 === 0 ? rotationRef.current + 180 : rotationRef.current + 360)
            setRevealed(true)
            onFinished?.()
            return
        }

        const timers: ReturnType<typeof setTimeout>[] = []
        // A first open is already face-down and just holds for a beat. A respin
        // still has the previous weapon facing the viewer, so it turns the card
        // back over first and the reels wait out that flip — otherwise the new
        // draw's slots would be running under a card still showing the old pull.
        let reelsBeginMs = INTRO_HOLD_MS
        if (!first) {
            flipTo(rotationRef.current + 180) // back to face-down
            reelsBeginMs = FLIP_MS
        }
        REEL_START_MS.forEach((start, index) => {
            timers.push(
                setTimeout(() => {
                    setStartedCount((n) => Math.max(n, index + 1))
                    // One start, then the loop under it. The loop is started on
                    // the first reel only -- three overlapping copies of the
                    // same bed is noise, not three reels.
                    sound.play('reelStart', 0.5)
                    if (index === 0) sound.loop('reelLoop', 0.35)
                }, reelsBeginMs + start),
            )
            timers.push(
                setTimeout(() => {
                    setSettledCount((n) => Math.max(n, index + 1))
                    // The Freak Mode tick as each reel lands, and the loop cut
                    // on the last one so the silence before the flip is real.
                    sound.play('tick', 0.55)
                    if (index === REEL_START_MS.length - 1) sound.stop('reelLoop')
                }, reelsBeginMs + start + REEL_SPIN_MS),
            )
        })
        // Flip to the weapon once every reel has settled.
        timers.push(
            setTimeout(() => {
                flipTo(rotationRef.current + 180)
                setRevealed(true)
                sound.stop('reelLoop')
                sound.play('reveal', 0.6)
                onFinished?.()
            }, reelsBeginMs + FLIP_AT_MS),
        )
        return () => {
            timers.forEach(clearTimeout)
            sound.stop('reelLoop')
        }
    }, [revealKey])

    const done = revealed

    // What the front face shows, which lags the `weapon` prop on purpose.
    //
    // A respin swaps `weapon` the instant the server answers, and at that
    // moment the face is still turned towards the viewer, fading out as the
    // card rotates back to the reels. Reading the prop directly, the new
    // skin's picture was already in that fade -- so the pull was visible for a
    // few hundred milliseconds before the slots had spun for it, which is the
    // one thing the animation exists to withhold.
    //
    // Advanced only at the reveal, so the outgoing face keeps showing the pull
    // the player has already seen.
    const [shown, setShown] = useState(weapon)
    useEffect(() => {
        if (done) setShown(weapon)
    }, [done, weapon])
    const glow = rarityGlow(weapon.rarity)
    // The card art for the face turned up, keyed off the weapon the reveal is
    // actually showing rather than the prop, so a respin does not repaint the
    // outgoing face in the incoming rarity.
    const art = cardArt(shown.rarity)

    return (
        <div className='flex h-full min-h-0 animate-zoom-in flex-col items-center gap-3 select-none'>
            {/* The card takes whatever height is left once the slots, the trait
                block and the keep/respin buttons have taken theirs, holding
                300:420 so the printed art never stretches. Capped at 520 so it
                does not balloon on a tall screen.

                Bigger is the point: the frame is a border drawn around the
                weapon, and at 300px wide the window inside it left the weapon
                smaller than the border around it. */}
            <div className='flex min-h-0 w-full flex-1 items-center justify-center'>
                <div
                    className='relative h-full max-h-[620px] [perspective:1200px]'
                    // Inline, NOT `aspect-[300/420]`: `.postcssrc` runs
                    // postcss-aspect-ratio-polyfill, which rewrites the utility
                    // into a padding hack computing height from width -- the
                    // reverse of what is wanted here, and it collapsed the card
                    // to zero width. An inline style never reaches postcss, so
                    // the native property applies and the width follows the
                    // height the flex row hands down.
                    style={{ aspectRatio: '300 / 420' }}
                    onClick={done ? undefined : skip}
                >
                {/* Rarity burst: a coloured flash that blooms out from behind the
                    card the moment it turns over, so a rare pull reads as an event
                    rather than a quiet image swap. */}
                <div
                    className='pointer-events-none absolute -inset-[45%] -z-10 transition-all duration-1000 ease-out'
                    style={{
                        opacity: done ? 0.6 : 0,
                        transform: done ? 'scale(1.05)' : 'scale(0.7)',
                        background: `radial-gradient(circle at 50% 46%, ${rarityColor(weapon.rarity)}66, transparent 58%)`,
                    }}
                />
                <div
                    className='relative h-full w-full transition-transform duration-700 [transform-style:preserve-3d]'
                    style={{ transform: `rotateY(${rotation}deg)` }}
                >
                    {/* Face down: the printed card back, and nothing else. The
                        slots used to be laid over this face; they are their own
                        block below the card now, so the sealed card is just the
                        card -- which is the whole point of having art for it. */}
                    <div className='absolute inset-0 overflow-hidden bg-black [backface-visibility:hidden]'>
                        <img src={CARD_BACK} alt='' aria-hidden='true' className='h-full w-full object-cover' />
                        {/* The card's edge, drawn because the art will not draw it:
                            `common_back.webp` bakes black bands into its top and
                            bottom eighth, and on a black page those are invisible
                            -- so the back read as a small square while the front,
                            whose identical bands carry the skull and the wordmark,
                            read as a tall card. Same element both times; only the
                            visible bounds differed, which is what made the flip
                            look like a resize. Neutral white, never a rarity
                            colour: this face must not hint at the pull. */}
                        <div className='pointer-events-none absolute inset-0 border-2 border-white/20' />
                    </div>

                    {/* Face up: the weapon, in its rarity's card.

                        Three layers, back to front: the rarity's printed
                        background, the weapon in the frame's transparent window,
                        then the frame itself. The rarity art and the weapon only
                        appear once `done` is set -- the moment of the reveal.
                        Without that gate a respin updates the weapon prop
                        immediately, so the new rarity's card was visible on this
                        face as it turned away, spoiling the pull before the slots
                        had spun for it. */}
                    <div className='absolute inset-0 overflow-hidden bg-black [backface-visibility:hidden] [transform:rotateY(180deg)]'>
                        <img
                            src={done ? art.background : CARD_BACK}
                            alt=''
                            aria-hidden='true'
                            className='absolute inset-0 h-full w-full object-cover'
                        />
                        {/* The weapon sits in the frame's transparent window,
                            which the art puts at 14.3% in from the top and the
                            bottom (measured off the alpha channel, not guessed),
                            so it is never half-hidden behind a printed bar. */}
                        <div
                            className='absolute inset-x-0 bottom-[14.3%] top-[14.3%] flex items-center justify-center transition-opacity duration-300'
                            style={{ opacity: done ? 1 : 0 }}
                        >
                            {shown.gif_uri ? (
                                <img
                                    src={shown.gif_uri}
                                    alt={shown.name ?? ''}
                                    className='max-h-full max-w-full object-contain'
                                />
                            ) : (
                                <span className='text-sm uppercase text-white/40'>no preview</span>
                            )}
                        </div>
                        <img
                            src={art.frame}
                            alt=''
                            aria-hidden='true'
                            className='pointer-events-none absolute inset-0 h-full w-full object-cover'
                        />
                        {/* A one-shot light sweep as the card turns, so the weapon
                            lands with a bit of sheen rather than flat. */}
                        <div
                            className='pointer-events-none absolute inset-y-0 left-0 w-3/5 bg-[linear-gradient(115deg,transparent_42%,rgba(255,255,255,0.16)_50%,transparent_58%)]'
                            style={{
                                transform: done ? 'translateX(240%)' : 'translateX(-120%)',
                                transition: 'transform 1.1s ease 0.5s',
                            }}
                        />
                        {/* Rarity edge, drawn over the frame so the card is rimmed
                            in the colour it just paid out. */}
                        <div
                            className='pointer-events-none absolute inset-0 border-2 transition-colors duration-300'
                            style={{
                                borderColor: done ? rarityColor(shown.rarity) : 'transparent',
                                boxShadow: done ? `inset ${glow}` : 'none',
                            }}
                        />
                        </div>
                    </div>
                </div>
            </div>

            {/* The slots, below the card rather than over it. Laid over it they
                had 276px to fit three columns of trait values into, which cut
                "Shotgun" down to "Shotg..."; here the block owns its own width
                and each reel gets the room its longest value needs. */}
            <div className='w-[360px] max-w-full shrink-0 border border-white/15 bg-black/85 px-2 py-2 shadow-[0_0_30px_-8px_rgba(3,206,255,0.4)]'>
                <div className='flex flex-row items-start divide-x divide-white/10'>
                    {reels.map((reel, index) => {
                        const state: ReelState =
                            index < settledCount ? 'settled' : index < startedCount ? 'active' : 'pending'
                        return (
                            <SlotReel
                                // Keyed by the draw so every spin remounts fresh.
                                key={`${reel.label}-${revealKey}`}
                                reel={reel}
                                spinMs={REEL_SPIN_MS}
                                state={state}
                            />
                        )
                    })}
                </div>
            </div>

            <div className='h-4 shrink-0 text-[10px] uppercase tracking-widest text-white/30'>
                {done ? '' : 'Tap to skip'}
            </div>

            <div
                className={twMerge(
                    'flex shrink-0 flex-col items-center gap-2 transition-opacity duration-500',
                    done ? 'opacity-100' : 'opacity-0',
                )}
            >
                {shown.rarity && (
                    <div
                        className='text-base font-black uppercase tracking-[0.28em]'
                        style={{ color: rarityColor(shown.rarity), textShadow: glow }}
                    >
                        {shown.rarity}
                    </div>
                )}
                <div className='text-2xl font-black uppercase tracking-[0.05em] text-center leading-none'>
                    {shown.name ?? 'Weapon'}
                </div>
                <div className='flex flex-row gap-4 text-xs uppercase tracking-wide text-white/50'>
                    {shown.weapon_type && <span>{shown.weapon_type}</span>}
                    {shown.erb && <span className='text-green-400'>+{shown.erb} earnings</span>}
                </div>
                {/* Skins have no mint: they are ours, not the chain's, so there
                    is no address to link to, and nothing is said about it -- a
                    line reading "not on chain" is a caveat on a prize, at the
                    one moment the player should just be looking at what they
                    won. Still guarded: rendered unconditionally, the `.slice`
                    threw on null and took the whole reveal down to a black
                    screen -- the pack had been paid for by then. */}
                {shown.mint && (
                    <a
                        href={`https://explorer.solana.com/address/${shown.mint}`}
                        target='_blank'
                        rel='noreferrer'
                        title={shown.mint}
                        className='text-[11px] font-mono text-white/30 hover:text-white/70'
                    >
                        {shown.mint.slice(0, 6)}...{shown.mint.slice(-6)}
                    </a>
                )}
            </div>
        </div>
    )
}
